/**
 * useAskVector — the React binding for Ask Vector (section 19c), now a REAL
 * hybrid RAG surface.
 *
 * On each question it BOTH:
 *  1. retrieves the most relevant team entities CLIENT-SIDE (ranked by
 *     {@link rankRefs}) + a relationships block from the entity graph, and
 *  2. queries the Bedrock knowledge base (RetrieveAndGenerate) for a grounded
 *     answer + citations.
 *
 * It then synthesizes ONE answer via REAL Bedrock converse, feeding it both the
 * client-side entity context AND the KB-grounded answer/passages, and appends an
 * assistant turn that cites BOTH — the retrieved {@link EntityRef}s (deep-linked
 * team entities) and the KB {@link KbCitation}s (source URIs + snippets). If the
 * KB is unavailable it degrades gracefully to entity-only grounding; if the
 * synthesis converse fails but the KB answered, the KB answer is used as a
 * fallback. The transcript is ephemeral (session only).
 */

import { useCallback, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import type { KbCitation } from '../../api';
import { useContextSnapshot } from './useContextSnapshot';
import { formatContext, rankRefs } from './contextRetriever';
import {
  buildEntityGraph,
  formatRelationships,
  graphSourceFromSnapshot,
} from '../graph/graphModel';
import {
  ANSWER_MAX_TOKENS,
  ANSWER_TEMPERATURE,
  buildAskPrompt,
  buildAskSystemPrompt,
  formatKbSources,
  friendlyInferenceError,
} from './intelligenceModel';
import type { AskTurn } from './types';

/** How many retrieved entities feed a single answer. */
const RETRIEVE_LIMIT = 20;
/** How many prior turns are replayed for light conversational continuity. */
const HISTORY_TURNS = 6;
/** How many KB chunks to surface as sources. */
const KB_RESULTS = 6;

export interface UseAskVectorResult {
  turns: AskTurn[];
  loading: boolean;
  asking: boolean;
  error: string | null;
  ask: (question: string) => Promise<void>;
  clear: () => void;
}

export function useAskVector(): UseAskVectorResult {
  const { refs, snapshot, loading, error, converse, kbRetrieveAndGenerate } =
    useContextSnapshot();
  const graph = useMemo(
    () => (snapshot ? buildEntityGraph(graphSourceFromSnapshot(snapshot)) : null),
    [snapshot],
  );
  const [turns, setTurns] = useState<AskTurn[]>([]);
  const [asking, setAsking] = useState(false);

  const ask = useCallback(
    async (question: string) => {
      const q = question.trim();
      if (!q || asking) return;

      const userTurn: AskTurn = {
        id: `q_${uuid()}`,
        role: 'user',
        content: q,
        sources: [],
        createdAt: new Date().toISOString(),
      };
      const priorForHistory = turns
        .filter((t) => !(t.role === 'assistant' && t.error))
        .slice(-HISTORY_TURNS)
        .map((t) => ({ role: t.role, content: t.content }));
      setTurns((prev) => [...prev, userTurn]);
      setAsking(true);

      // (1) Client-side structured-entity retrieval + relationships.
      const retrieved = rankRefs(refs, q, RETRIEVE_LIMIT);
      const baseContext = formatContext(retrieved, { perKindLimit: 24 });
      const relationships = graph
        ? formatRelationships(graph, retrieved.map((r) => r.id))
        : '';
      const context = relationships
        ? `${baseContext}\n\n${relationships}`
        : baseContext;

      // (2) KB-grounded answer + citations (best-effort; degrade if unavailable).
      let kbAnswer = '';
      let kbCitations: KbCitation[] = [];
      try {
        const kb = await kbRetrieveAndGenerate(q);
        kbAnswer = kb.answer.trim();
        kbCitations = kb.citations;
      } catch {
        /* KB unavailable (access/model not enabled) — entity-only grounding */
      }
      const kbSources = kbCitations.flatMap((c) => c.sources);
      const kbContext = kbAnswer
        ? [
            'KNOWLEDGE BASE (grounded answer):',
            kbAnswer,
            formatKbSources(kbSources, KB_RESULTS),
          ]
            .filter(Boolean)
            .join('\n')
        : '';

      // (3) Synthesize one answer over BOTH sources.
      try {
        const answer = await converse(
          buildAskPrompt(context, q, priorForHistory, kbContext),
          buildAskSystemPrompt(),
          { maxTokens: ANSWER_MAX_TOKENS, temperature: ANSWER_TEMPERATURE },
        );
        setTurns((prev) => [
          ...prev,
          {
            id: `a_${uuid()}`,
            role: 'assistant',
            content: answer.trim() || kbAnswer || '_(empty response)_',
            sources: retrieved,
            ...(kbCitations.length ? { kbCitations } : {}),
            createdAt: new Date().toISOString(),
          },
        ]);
      } catch (e) {
        const raw = e instanceof Error ? e.message : 'Inference request failed.';
        // If the KB answered, prefer showing it over a bare error.
        if (kbAnswer) {
          setTurns((prev) => [
            ...prev,
            {
              id: `a_${uuid()}`,
              role: 'assistant',
              content: kbAnswer,
              sources: retrieved,
              ...(kbCitations.length ? { kbCitations } : {}),
              createdAt: new Date().toISOString(),
            },
          ]);
        } else {
          setTurns((prev) => [
            ...prev,
            {
              id: `a_${uuid()}`,
              role: 'assistant',
              content: friendlyInferenceError(raw),
              error: true,
              sources: retrieved,
              createdAt: new Date().toISOString(),
            },
          ]);
        }
      } finally {
        setAsking(false);
      }
    },
    [asking, turns, refs, graph, converse, kbRetrieveAndGenerate],
  );

  const clear = useCallback(() => setTurns([]), []);

  return { turns, loading, asking, error, ask, clear };
}
